/**
 * ─── HRIS Biometric Device Configuration ────────────────────
 * Manage biometric time-clock devices: ZKTeco, Suprema, Hikvision.
 * Configure connection parameters, sync intervals, and device types.
 */

import { useState } from 'react'
import { Card, CardHeader, Badge, Button, Input, Modal } from '@repo/ui-core'
import { Fingerprint, Plus, Pencil, Trash2, Wifi, Cable, Router, Cloud, RefreshCw } from 'lucide-react'

interface DeviceRecord {
  id: string
  deviceCode: string
  name: string
  deviceType: string
  protocol: string
  ipAddress: string
  port: number
  serialNumber: string
  location: string
  isActive: boolean
  terminalId: string
  lastSyncAt: number | null
  syncIntervalMinutes: number
}

const STORAGE_KEY = 'hris_devices'
const DEFAULT_DEVICES: DeviceRecord[] = [
  { id: 'dev-1', deviceCode: 'ZK-001', name: 'ZKTeco Main Entrance', deviceType: 'zkteco', protocol: 'tcp', ipAddress: '192.168.1.100', port: 4370, serialNumber: 'ZK2025A001', location: 'Main Entrance', isActive: true, terminalId: 'T1', lastSyncAt: Date.now(), syncIntervalMinutes: 15 },
  { id: 'dev-2', deviceCode: 'SP-001', name: 'Suprema Back Office', deviceType: 'suprema', protocol: 'wifi', ipAddress: '192.168.1.101', port: 443, serialNumber: 'SP2025B002', location: 'Back Office Door', isActive: true, terminalId: 'T2', lastSyncAt: Date.now() - 3600000, syncIntervalMinutes: 30 },
]

function load(): DeviceRecord[] {
  try { const r = localStorage.getItem(STORAGE_KEY); return r ? JSON.parse(r) : [...DEFAULT_DEVICES] }
  catch { return [...DEFAULT_DEVICES] }
}
function save(d: DeviceRecord[]) { localStorage.setItem(STORAGE_KEY, JSON.stringify(d)) }

const DEVICE_TYPE_LABELS: Record<string, string> = {
  zkteco: 'ZKTeco', suprema: 'Suprema', hikvision: 'Hikvision',
  fingerprint: 'Fingerprint', face_recognition: 'Face Recognition', generic: 'Generic',
}
const PROTOCOL_ICONS: Record<string, any> = { tcp: Cable, usb: Cable, wifi: Wifi, cloud_api: Cloud }
const PROTOCOL_LABELS: Record<string, string> = { tcp: 'TCP/IP', usb: 'USB', wifi: 'WiFi', cloud_api: 'Cloud API' }

export function HrisDevicePage() {
  const [devices, setDevices] = useState<DeviceRecord[]>(load)
  const [showModal, setShowModal] = useState(false)
  const [editId, setEditId] = useState<string | null>(null)
  const [form, setForm] = useState({
    deviceCode: '', name: '', deviceType: 'zkteco', protocol: 'tcp',
    ipAddress: '', port: 4370, serialNumber: '', location: '',
    isActive: true, terminalId: '', syncIntervalMinutes: 15,
  })

  const persist = (d: DeviceRecord[]) => { setDevices(d); save(d) }

  function reset() {
    setForm({ deviceCode: '', name: '', deviceType: 'zkteco', protocol: 'tcp',
      ipAddress: '', port: 4370, serialNumber: '', location: '',
      isActive: true, terminalId: '', syncIntervalMinutes: 15 })
    setEditId(null)
  }

  function handleSave() {
    if (!form.name || !form.deviceCode) return
    const entry: DeviceRecord = {
      id: editId || 'dev-' + Date.now(),
      ...form,
      lastSyncAt: editId ? devices.find(d => d.id === editId)?.lastSyncAt ?? null : null,
    }
    const updated = editId
      ? devices.map(d => d.id === editId ? entry : d)
      : [entry, ...devices]
    persist(updated)
    setShowModal(false)
    reset()
  }

  function handleDelete(id: string) {
    if (!confirm('Delete this device?')) return
    persist(devices.filter(d => d.id !== id))
  }

  function handleSync(id: string) {
    const device = devices.find(d => d.id === id)
    if (!device) return
    // Simulate biometric device connection + log pull
    const now = Date.now()
    const syncMsg = `[${new Date(now).toLocaleTimeString()}] Device "${device.name}" (${device.deviceCode})
` +
      `  → Connected via ${device.protocol.toUpperCase()}: ${device.ipAddress}:${device.port}
` +
      `  → Pulled attendance logs since ${device.lastSyncAt ? new Date(device.lastSyncAt).toLocaleString() : 'last sync'}
` +
      `  → Logs retrieved: ${Math.floor(Math.random() * 25) + 5} records
` +
      `  → Sync completed successfully ✓`
    
    alert(syncMsg)
    persist(devices.map(d => d.id === id ? { ...d, lastSyncAt: now } : d))
  }

  function startEdit(d: DeviceRecord) {
    setForm({
      deviceCode: d.deviceCode, name: d.name, deviceType: d.deviceType, protocol: d.protocol,
      ipAddress: d.ipAddress, port: d.port, serialNumber: d.serialNumber, location: d.location,
      isActive: d.isActive, terminalId: d.terminalId, syncIntervalMinutes: d.syncIntervalMinutes,
    })
    setEditId(d.id)
    setShowModal(true)
  }

  const lastSyncText = (d: DeviceRecord) => {
    if (!d.lastSyncAt) return 'Never'
    const mins = Math.round((Date.now() - d.lastSyncAt) / 60000)
    return mins < 1 ? 'Just now' : mins < 60 ? `${mins}m ago` : `${Math.round(mins / 60)}h ago`
  }

  return (
    <div className="p-6">
      <div className="mb-6 flex items-center justify-between">
        <div>
          <h2 className="text-2xl font-bold text-gray-900">Biometric Devices</h2>
          <p className="mt-1 text-sm text-gray-500">Configure fingerprint and face recognition time-clocks (ZKTeco, Suprema, Hikvision)</p>
        </div>
        <Button onClick={() => { reset(); setShowModal(true) }} icon={<Plus className="h-4 w-4" />}>
          Add Device
        </Button>
      </div>

      {/* Stats */}
      <div className="mb-6 grid grid-cols-2 gap-3 sm:grid-cols-4">
        {[
          ['Total Devices', devices.length, 'blue'],
          ['Active', devices.filter(d => d.isActive).length, 'green'],
          ['ZKTeco', devices.filter(d => d.deviceType === 'zkteco').length, 'amber'],
          ['Brands', new Set(devices.map(d => d.deviceType)).size, 'purple'],
        ].map(([label, value, color]) => (
          <div key={label as string} className={`rounded-xl border border-${color}-200 bg-${color}-50 p-4`}>
            <p className={`text-xs text-${color}-600`}>{label as string}</p>
            <p className={`mt-1 text-xl font-bold text-${color}-700`}>{value as number}</p>
          </div>
        ))}
      </div>

      <div className="grid grid-cols-1 gap-4 sm:grid-cols-2 lg:grid-cols-3">
        {devices.map(d => {
          const PIcon = PROTOCOL_ICONS[d.protocol] ?? Router
          return (
            <div key={d.id} className="rounded-xl border border-gray-200 bg-white p-5 shadow-sm transition-shadow hover:shadow-md">
              <div className="mb-3 flex items-center justify-between">
                <div className="flex items-center gap-3">
                  <div className={`rounded-lg p-2.5 ${d.isActive ? 'bg-green-100' : 'bg-gray-100'}`}>
                    <Fingerprint className={`h-5 w-5 ${d.isActive ? 'text-green-600' : 'text-gray-400'}`} />
                  </div>
                  <div>
                    <h3 className="font-semibold text-gray-900">{d.name}</h3>
                    <p className="text-xs text-gray-500">{d.deviceCode}</p>
                  </div>
                </div>
                <Badge color={d.isActive ? 'green' : 'gray'}>{d.isActive ? 'Active' : 'Offline'}</Badge>
              </div>
              <div className="space-y-1.5 text-sm">
                <div className="flex justify-between">
                  <span className="text-gray-500">Type</span>
                  <span className="font-medium">{DEVICE_TYPE_LABELS[d.deviceType] ?? d.deviceType}</span>
                </div>
                <div className="flex justify-between">
                  <span className="text-gray-500">Protocol</span>
                  <span className="flex items-center gap-1 font-medium">
                    <PIcon className="h-3 w-3" /> {PROTOCOL_LABELS[d.protocol] ?? d.protocol}
                  </span>
                </div>
                {d.ipAddress && (
                  <div className="flex justify-between">
                    <span className="text-gray-500">IP:Port</span>
                    <span className="font-mono font-medium text-xs">{d.ipAddress}:{d.port}</span>
                  </div>
                )}
                <div className="flex justify-between">
                  <span className="text-gray-500">Location</span>
                  <span className="font-medium text-xs">{d.location}</span>
                </div>
                <div className="flex justify-between">
                  <span className="text-gray-500">Sync</span>
                  <span className="font-medium text-xs">Every {d.syncIntervalMinutes}m</span>
                </div>
                <div className="flex justify-between">
                  <span className="text-gray-500">Last Sync</span>
                  <span className="text-xs text-gray-400">{lastSyncText(d)}</span>
                </div>
                {d.terminalId && (
                  <div className="flex justify-between">
                    <span className="text-gray-500">Terminal ID</span>
                    <span className="font-mono text-xs">{d.terminalId}</span>
                  </div>
                )}
              </div>
              <div className="mt-4 flex gap-2 border-t border-gray-100 pt-3">
                <button onClick={() => handleSync(d.id)} className="rounded p-1.5 text-green-600 hover:bg-green-50" title="Sync Now">
                  <Cloud className="h-4 w-4" />
                </button>
                <button onClick={() => startEdit(d)} className="rounded p-1.5 text-blue-600 hover:bg-blue-50" title="Edit">
                  <Pencil className="h-4 w-4" />
                </button>
                <button onClick={() => handleDelete(d.id)} className="rounded p-1.5 text-red-600 hover:bg-red-50" title="Delete">
                  <Trash2 className="h-4 w-4" />
                </button>
              </div>
            </div>
          )
        })}
      </div>

      <Modal open={showModal} onClose={() => { setShowModal(false); reset() }}
        title={editId ? 'Edit Device' : 'Add Biometric Device'} size="lg"
        footer={
          <div className="flex gap-2">
            <Button variant="secondary" onClick={() => { setShowModal(false); reset() }}>Cancel</Button>
            <Button onClick={handleSave} icon={<Fingerprint className="h-4 w-4" />}>{editId ? 'Update' : 'Add'} Device</Button>
          </div>
        }>
        <div className="grid grid-cols-1 gap-3 sm:grid-cols-2">
          <Input label="Device Code" value={form.deviceCode} onChange={e => setForm({ ...form, deviceCode: e.target.value })} placeholder="ZK-001" />
          <Input label="Device Name" value={form.name} onChange={e => setForm({ ...form, name: e.target.value })} placeholder="Main Entrance" />
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">Device Type</label>
            <select value={form.deviceType} onChange={e => setForm({ ...form, deviceType: e.target.value })}
              className="block w-full rounded-lg border border-gray-300 px-3 py-2 text-sm">
              {Object.entries(DEVICE_TYPE_LABELS).map(([k, v]) => (
                <option key={k} value={k}>{v}</option>
              ))}
            </select>
          </div>
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">Protocol</label>
            <select value={form.protocol} onChange={e => setForm({ ...form, protocol: e.target.value })}
              className="block w-full rounded-lg border border-gray-300 px-3 py-2 text-sm">
              {Object.entries(PROTOCOL_LABELS).map(([k, v]) => (
                <option key={k} value={k}>{v}</option>
              ))}
            </select>
          </div>
          <Input label="IP Address" value={form.ipAddress} onChange={e => setForm({ ...form, ipAddress: e.target.value })} placeholder="192.168.1.100" />
          <Input label="Port" type="number" value={String(form.port)} onChange={e => setForm({ ...form, port: Number(e.target.value) })} />
          <Input label="Serial Number" value={form.serialNumber} onChange={e => setForm({ ...form, serialNumber: e.target.value })} />
          <Input label="Terminal ID" value={form.terminalId} onChange={e => setForm({ ...form, terminalId: e.target.value })} placeholder="T1" />
          <Input label="Location" value={form.location} onChange={e => setForm({ ...form, location: e.target.value })} placeholder="Main Entrance" />
          <Input label="Sync Interval (min)" type="number" value={String(form.syncIntervalMinutes)}
            onChange={e => setForm({ ...form, syncIntervalMinutes: Number(e.target.value) })} />
          <div className="flex items-center gap-2 pt-5">
            <input type="checkbox" checked={form.isActive} onChange={e => setForm({ ...form, isActive: e.target.checked })}
              className="h-4 w-4 rounded border-gray-300" />
            <label className="text-sm text-gray-700">Active</label>
          </div>
        </div>
      </Modal>
    </div>
  )
}
